import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ApiState,
  MutationState,
  DowngradeImpactPreviewDto,
  StripeAddressDto,
  StripeCheckoutDto,
  StripeInvoiceDto,
  StripePaymentMethodDto,
  StripePriceDto,
  StripePromoCodeDto,
  StripeSetupIntentDto,
  StripeSubscriptionDto,
  StripeSubscriptionResponseDto,
  StripeUpgradePreviewDto,
  SubscriptionExitFlowDto,
  WorkspaceUsageDto,
} from '@/features/billing/types';
import {
  createCheckout,
  createSetupIntent,
  createSubscription,
  detachPaymentMethod,
  downloadInvoicesZip,
  fetchAddress,
  fetchInvoices,
  fetchPaymentMethods,
  fetchPrices,
  fetchPromoCode,
  fetchSubscription,
  fetchUpgradePreview,
  fetchDowngradeImpactPreview,
  fetchUsage,
  acceptRetentionDowngrade,
  acceptRetentionPause,
  confirmRetentionCancellation,
  declineRetentionDowngrade,
  declineRetentionPause,
  resumeSubscription,
  resumeSubscriptionPause,
  setPaymentMethodDefault,
  startSubscriptionExitFlow,
  updateAddress,
  upgradeSubscription,
} from '@/features/billing/billingThunks';
import { logout } from '@/features/auth/authThunks';
import { switchWorkspace } from '@/features/workspace/workspaceSlice';
import {
  isSamePromoCode,
  normalizePromoCode,
  resolveCheckoutPromoCode,
} from '@/lib/checkoutPromoCode';

const emptyApiState = <T>(): ApiState<T> => ({
  data: null,
  error: null,
  loading: false,
});

/**
 * Garde le requestId de la dernière lecture émise : une réponse plus ancienne, d'un workspace
 * quitté depuis, ne doit pas écraser celle du workspace courant.
 */
type LatestApiState<T> = ApiState<T> & { requestId: string | null };

const emptyLatestApiState = <T>(): LatestApiState<T> => ({
  ...emptyApiState<T>(),
  requestId: null,
});

const emptyMutationState = (): MutationState => ({
  error: null,
  loading: false,
});

export interface BillingState {
  subscription: LatestApiState<StripeSubscriptionDto>;
  usage: LatestApiState<WorkspaceUsageDto>;
  upgradePreview: ApiState<StripeUpgradePreviewDto>;
  /** requestId de la dernière requête émise : une réponse plus ancienne décrirait un autre forfait. */
  downgradeImpactPreview: ApiState<DowngradeImpactPreviewDto> & { requestId: string | null };
  /** Un changement de code promo relance la lecture des tarifs : seule la dernière fait foi. */
  prices: LatestApiState<StripePriceDto[]> & { promoCode: string | null };
  promoCode: LatestApiState<StripePromoCodeDto>;
  /** Code validé dans le tunnel de paiement, envoyé à la souscription à la place de celui du workspace. */
  checkoutPromoCode: string | null;
  paymentMethods: LatestApiState<StripePaymentMethodDto[]>;
  invoices: ApiState<StripeInvoiceDto[]>;
  address: ApiState<StripeAddressDto>;
  setupIntent: ApiState<StripeSetupIntentDto>;
  checkout: ApiState<StripeCheckoutDto>;
  createSubscription: ApiState<StripeSubscriptionResponseDto>;
  exitFlow: ApiState<SubscriptionExitFlowDto>;
  upgrade: MutationState;
  resumeSubscription: MutationState;
  resumeSubscriptionPause: MutationState;
  setPaymentMethodDefault: MutationState;
  detachPaymentMethod: MutationState;
  downloadInvoicesZip: MutationState;
}

const initialState: BillingState = {
  subscription: emptyLatestApiState(),
  usage: emptyLatestApiState(),
  upgradePreview: emptyApiState(),
  downgradeImpactPreview: { ...emptyApiState(), requestId: null },
  prices: { ...emptyLatestApiState(), promoCode: null },
  promoCode: emptyLatestApiState(),
  checkoutPromoCode: null,
  paymentMethods: emptyLatestApiState(),
  invoices: emptyApiState(),
  address: emptyApiState(),
  setupIntent: emptyApiState(),
  checkout: emptyApiState(),
  createSubscription: emptyApiState(),
  exitFlow: emptyApiState(),
  upgrade: emptyMutationState(),
  resumeSubscription: emptyMutationState(),
  resumeSubscriptionPause: emptyMutationState(),
  setPaymentMethodDefault: emptyMutationState(),
  detachPaymentMethod: emptyMutationState(),
  downloadInvoicesZip: emptyMutationState(),
};

const slice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearSubscription: (state) => {
      state.subscription = emptyLatestApiState();
    },
    setCheckoutPromoCode: (state, action: PayloadAction<string | null>) => {
      state.checkoutPromoCode = action.payload;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSubscription.pending, (state, action) => {
      state.subscription.loading = true;
      state.subscription.error = null;
      state.subscription.requestId = action.meta.requestId;
    });
    builder.addCase(fetchSubscription.fulfilled, (state, action) => {
      if (state.subscription.requestId !== action.meta.requestId) return;
      state.subscription.loading = false;
      state.subscription.data = action.payload;
      state.subscription.error = null;
    });
    builder.addCase(fetchSubscription.rejected, (state, action) => {
      if (state.subscription.requestId !== action.meta.requestId) return;
      state.subscription.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.subscription.error =
        payload?.message ?? action.error.message ?? 'Failed to fetch subscription';
    });

    builder.addCase(fetchUsage.pending, (state, action) => {
      state.usage.loading = true;
      state.usage.error = null;
      state.usage.requestId = action.meta.requestId;
    });
    builder.addCase(fetchUsage.fulfilled, (state, action) => {
      if (state.usage.requestId !== action.meta.requestId) return;
      state.usage.loading = false;
      state.usage.data = action.payload;
      state.usage.error = null;
    });
    builder.addCase(fetchUsage.rejected, (state, action) => {
      if (state.usage.requestId !== action.meta.requestId) return;
      state.usage.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.usage.error = payload?.message ?? action.error.message ?? 'Failed to fetch usage';
    });

    builder.addCase(fetchUpgradePreview.pending, (state) => {
      state.upgradePreview.loading = true;
      state.upgradePreview.error = null;
    });
    builder.addCase(
      fetchUpgradePreview.fulfilled,
      (state, action: PayloadAction<StripeUpgradePreviewDto>) => {
        state.upgradePreview.loading = false;
        state.upgradePreview.data = action.payload;
        state.upgradePreview.error = null;
      },
    );
    builder.addCase(fetchUpgradePreview.rejected, (state, action) => {
      state.upgradePreview.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.upgradePreview.error =
        payload?.message ?? action.error.message ?? 'Failed to fetch upgrade preview';
    });

    // Un aller-retour rapide entre deux forfaits fait courir deux requêtes : seule la dernière décrit
    // le forfait que le client s'apprête à confirmer, les réponses en retard sont ignorées.
    builder.addCase(fetchDowngradeImpactPreview.pending, (state, action) => {
      state.downgradeImpactPreview.loading = true;
      state.downgradeImpactPreview.data = null;
      state.downgradeImpactPreview.error = null;
      state.downgradeImpactPreview.requestId = action.meta.requestId;
    });
    builder.addCase(fetchDowngradeImpactPreview.fulfilled, (state, action) => {
      if (state.downgradeImpactPreview.requestId !== action.meta.requestId) return;
      state.downgradeImpactPreview.loading = false;
      state.downgradeImpactPreview.data = action.payload;
      state.downgradeImpactPreview.error = null;
    });
    builder.addCase(fetchDowngradeImpactPreview.rejected, (state, action) => {
      if (state.downgradeImpactPreview.requestId !== action.meta.requestId) return;
      state.downgradeImpactPreview.loading = false;
      state.downgradeImpactPreview.data = null;
      const payload = action.payload as { message?: string } | undefined;
      state.downgradeImpactPreview.error =
        payload?.message ?? action.error.message ?? 'Failed to fetch downgrade impact preview';
    });

    builder.addCase(fetchPrices.pending, (state, action) => {
      state.prices.loading = true;
      state.prices.error = null;
      state.prices.requestId = action.meta.requestId;
    });
    builder.addCase(fetchPrices.fulfilled, (state, action) => {
      if (state.prices.requestId !== action.meta.requestId) return;
      state.prices.loading = false;
      state.prices.data = action.payload;
      state.prices.promoCode = normalizePromoCode(action.meta.arg.promoCode);
      state.prices.error = null;
    });
    builder.addCase(fetchPrices.rejected, (state, action) => {
      if (state.prices.requestId !== action.meta.requestId) return;
      state.prices.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.prices.error = payload?.message ?? action.error.message ?? 'Failed to fetch prices';
    });

    builder.addCase(fetchPromoCode.pending, (state, action) => {
      state.promoCode.loading = true;
      state.promoCode.error = null;
      state.promoCode.requestId = action.meta.requestId;
    });
    builder.addCase(fetchPromoCode.fulfilled, (state, action) => {
      if (state.promoCode.requestId !== action.meta.requestId) return;
      state.promoCode.loading = false;
      state.promoCode.data = action.payload;
      state.promoCode.error = null;
    });
    builder.addCase(fetchPromoCode.rejected, (state, action) => {
      if (state.promoCode.requestId !== action.meta.requestId) return;
      state.promoCode.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.promoCode.error =
        payload?.message ?? action.error.message ?? 'Failed to fetch promo code';
    });

    builder.addCase(fetchPaymentMethods.pending, (state, action) => {
      state.paymentMethods.loading = true;
      state.paymentMethods.error = null;
      state.paymentMethods.requestId = action.meta.requestId;
    });
    builder.addCase(fetchPaymentMethods.fulfilled, (state, action) => {
      if (state.paymentMethods.requestId !== action.meta.requestId) return;
      state.paymentMethods.loading = false;
      state.paymentMethods.data = action.payload;
      state.paymentMethods.error = null;
    });
    builder.addCase(fetchPaymentMethods.rejected, (state, action) => {
      if (state.paymentMethods.requestId !== action.meta.requestId) return;
      state.paymentMethods.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.paymentMethods.error =
        payload?.message ?? action.error.message ?? 'Failed to fetch payment methods';
    });

    builder.addCase(fetchInvoices.pending, (state) => {
      state.invoices.loading = true;
      state.invoices.error = null;
    });
    builder.addCase(fetchInvoices.fulfilled, (state, action: PayloadAction<StripeInvoiceDto[]>) => {
      state.invoices.loading = false;
      state.invoices.data = action.payload;
      state.invoices.error = null;
    });
    builder.addCase(fetchInvoices.rejected, (state, action) => {
      state.invoices.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.invoices.error = payload?.message ?? action.error.message ?? 'Failed to fetch invoices';
    });

    builder.addCase(fetchAddress.pending, (state) => {
      state.address.loading = true;
      state.address.error = null;
    });
    builder.addCase(fetchAddress.fulfilled, (state, action: PayloadAction<StripeAddressDto>) => {
      state.address.loading = false;
      state.address.data = action.payload;
      state.address.error = null;
    });
    builder.addCase(fetchAddress.rejected, (state, action) => {
      state.address.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.address.error = payload?.message ?? action.error.message ?? 'Failed to fetch address';
    });

    builder.addCase(updateAddress.pending, (state) => {
      state.address.loading = true;
      state.address.error = null;
    });
    builder.addCase(updateAddress.fulfilled, (state, action: PayloadAction<StripeAddressDto>) => {
      state.address.loading = false;
      state.address.data = action.payload;
      state.address.error = null;
    });
    builder.addCase(updateAddress.rejected, (state, action) => {
      state.address.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.address.error = payload?.message ?? action.error.message ?? 'Failed to update address';
    });

    builder.addCase(upgradeSubscription.pending, (state) => {
      state.upgrade.loading = true;
      state.upgrade.error = null;
    });
    builder.addCase(
      upgradeSubscription.fulfilled,
      (state, _action: PayloadAction<StripeSubscriptionResponseDto>) => {
        state.upgrade.loading = false;
        state.upgrade.error = null;
      },
    );
    builder.addCase(upgradeSubscription.rejected, (state, action) => {
      state.upgrade.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.upgrade.error =
        payload?.message ?? action.error.message ?? 'Failed to upgrade subscription';
    });

    builder.addCase(resumeSubscription.pending, (state) => {
      state.resumeSubscription.loading = true;
      state.resumeSubscription.error = null;
    });
    builder.addCase(
      resumeSubscription.fulfilled,
      (state, action: PayloadAction<StripeSubscriptionDto>) => {
        state.resumeSubscription.loading = false;
        state.resumeSubscription.error = null;
        state.subscription.data = action.payload;
      },
    );
    builder.addCase(resumeSubscription.rejected, (state, action) => {
      state.resumeSubscription.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.resumeSubscription.error =
        payload?.message ?? action.error.message ?? 'Failed to resume subscription';
    });

    builder.addCase(resumeSubscriptionPause.pending, (state) => {
      state.resumeSubscriptionPause.loading = true;
      state.resumeSubscriptionPause.error = null;
    });
    builder.addCase(
      resumeSubscriptionPause.fulfilled,
      (state, action: PayloadAction<StripeSubscriptionDto>) => {
        state.resumeSubscriptionPause.loading = false;
        state.resumeSubscriptionPause.error = null;
        state.subscription.data = action.payload;
      },
    );
    builder.addCase(resumeSubscriptionPause.rejected, (state, action) => {
      state.resumeSubscriptionPause.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.resumeSubscriptionPause.error =
        payload?.message ?? action.error.message ?? 'Failed to resume paused subscription';
    });

    builder.addCase(createSetupIntent.pending, (state) => {
      state.setupIntent.loading = true;
      state.setupIntent.error = null;
    });
    builder.addCase(
      createSetupIntent.fulfilled,
      (state, action: PayloadAction<StripeSetupIntentDto>) => {
        state.setupIntent.loading = false;
        state.setupIntent.data = action.payload;
        state.setupIntent.error = null;
      },
    );
    builder.addCase(createSetupIntent.rejected, (state, action) => {
      state.setupIntent.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.setupIntent.error =
        payload?.message ?? action.error.message ?? 'Failed to create setup intent';
    });

    builder.addCase(createCheckout.pending, (state) => {
      state.checkout.loading = true;
      state.checkout.error = null;
    });
    builder.addCase(createCheckout.fulfilled, (state, action: PayloadAction<StripeCheckoutDto>) => {
      state.checkout.loading = false;
      state.checkout.data = action.payload;
      state.checkout.error = null;
    });
    builder.addCase(createCheckout.rejected, (state, action) => {
      state.checkout.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.checkout.error =
        payload?.message ?? action.error.message ?? 'Failed to create checkout';
    });

    builder.addCase(createSubscription.pending, (state) => {
      state.createSubscription.loading = true;
      state.createSubscription.error = null;
    });
    builder.addCase(
      createSubscription.fulfilled,
      (state, action: PayloadAction<StripeSubscriptionResponseDto>) => {
        state.createSubscription.loading = false;
        state.createSubscription.data = action.payload;
        state.createSubscription.error = null;
      },
    );
    builder.addCase(createSubscription.rejected, (state, action) => {
      state.createSubscription.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.createSubscription.error =
        payload?.message ?? action.error.message ?? 'Failed to create subscription';
    });

    builder.addCase(startSubscriptionExitFlow.pending, (state) => {
      state.exitFlow.loading = true;
      state.exitFlow.error = null;
    });
    builder.addCase(
      startSubscriptionExitFlow.fulfilled,
      (state, action: PayloadAction<SubscriptionExitFlowDto>) => {
        state.exitFlow.loading = false;
        state.exitFlow.data = action.payload;
        state.exitFlow.error = null;
      },
    );
    builder.addCase(startSubscriptionExitFlow.rejected, (state, action) => {
      state.exitFlow.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.exitFlow.error =
        payload?.message ?? action.error.message ?? 'Failed to start subscription exit flow';
    });

    builder.addCase(acceptRetentionDowngrade.pending, (state) => {
      state.exitFlow.loading = true;
      state.exitFlow.error = null;
    });
    builder.addCase(
      acceptRetentionDowngrade.fulfilled,
      (state, action: PayloadAction<SubscriptionExitFlowDto>) => {
        state.exitFlow.loading = false;
        state.exitFlow.data = action.payload;
        state.exitFlow.error = null;
      },
    );
    builder.addCase(acceptRetentionDowngrade.rejected, (state, action) => {
      state.exitFlow.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.exitFlow.error =
        payload?.message ?? action.error.message ?? 'Failed to accept retention downgrade';
    });

    builder.addCase(declineRetentionDowngrade.pending, (state) => {
      state.exitFlow.loading = true;
      state.exitFlow.error = null;
    });
    builder.addCase(
      declineRetentionDowngrade.fulfilled,
      (state, action: PayloadAction<SubscriptionExitFlowDto>) => {
        state.exitFlow.loading = false;
        state.exitFlow.data = action.payload;
        state.exitFlow.error = null;
      },
    );
    builder.addCase(declineRetentionDowngrade.rejected, (state, action) => {
      state.exitFlow.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.exitFlow.error =
        payload?.message ?? action.error.message ?? 'Failed to decline retention downgrade';
    });

    builder.addCase(acceptRetentionPause.pending, (state) => {
      state.exitFlow.loading = true;
      state.exitFlow.error = null;
    });
    builder.addCase(
      acceptRetentionPause.fulfilled,
      (state, action: PayloadAction<SubscriptionExitFlowDto>) => {
        state.exitFlow.loading = false;
        state.exitFlow.data = action.payload;
        state.exitFlow.error = null;
      },
    );
    builder.addCase(acceptRetentionPause.rejected, (state, action) => {
      state.exitFlow.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.exitFlow.error =
        payload?.message ?? action.error.message ?? 'Failed to accept retention pause';
    });

    builder.addCase(declineRetentionPause.pending, (state) => {
      state.exitFlow.loading = true;
      state.exitFlow.error = null;
    });
    builder.addCase(
      declineRetentionPause.fulfilled,
      (state, action: PayloadAction<SubscriptionExitFlowDto>) => {
        state.exitFlow.loading = false;
        state.exitFlow.data = action.payload;
        state.exitFlow.error = null;
      },
    );
    builder.addCase(declineRetentionPause.rejected, (state, action) => {
      state.exitFlow.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.exitFlow.error =
        payload?.message ?? action.error.message ?? 'Failed to decline retention pause';
    });

    builder.addCase(confirmRetentionCancellation.pending, (state) => {
      state.exitFlow.loading = true;
      state.exitFlow.error = null;
    });
    builder.addCase(
      confirmRetentionCancellation.fulfilled,
      (state, action: PayloadAction<SubscriptionExitFlowDto>) => {
        state.exitFlow.loading = false;
        state.exitFlow.data = action.payload;
        state.exitFlow.error = null;
      },
    );
    builder.addCase(confirmRetentionCancellation.rejected, (state, action) => {
      state.exitFlow.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.exitFlow.error =
        payload?.message ?? action.error.message ?? 'Failed to confirm retention cancellation';
    });

    builder.addCase(setPaymentMethodDefault.pending, (state) => {
      state.setPaymentMethodDefault.loading = true;
      state.setPaymentMethodDefault.error = null;
    });
    builder.addCase(setPaymentMethodDefault.fulfilled, (state) => {
      state.setPaymentMethodDefault.loading = false;
      state.setPaymentMethodDefault.error = null;
    });
    builder.addCase(setPaymentMethodDefault.rejected, (state, action) => {
      state.setPaymentMethodDefault.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.setPaymentMethodDefault.error =
        payload?.message ?? action.error.message ?? 'Failed to set default payment method';
    });

    builder.addCase(detachPaymentMethod.pending, (state) => {
      state.detachPaymentMethod.loading = true;
      state.detachPaymentMethod.error = null;
    });
    builder.addCase(detachPaymentMethod.fulfilled, (state, action) => {
      state.detachPaymentMethod.loading = false;
      state.detachPaymentMethod.error = null;
      const { paymentMethodId } = action.meta.arg;
      if (state.paymentMethods.data) {
        state.paymentMethods.data = state.paymentMethods.data.filter(
          (pm) => pm.id !== paymentMethodId,
        );
      }
    });
    builder.addCase(detachPaymentMethod.rejected, (state, action) => {
      state.detachPaymentMethod.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.detachPaymentMethod.error =
        payload?.message ?? action.error.message ?? 'Failed to remove payment method';
    });

    builder.addCase(downloadInvoicesZip.pending, (state) => {
      state.downloadInvoicesZip.loading = true;
      state.downloadInvoicesZip.error = null;
    });
    builder.addCase(downloadInvoicesZip.fulfilled, (state) => {
      state.downloadInvoicesZip.loading = false;
      state.downloadInvoicesZip.error = null;
    });
    builder.addCase(downloadInvoicesZip.rejected, (state, action) => {
      state.downloadInvoicesZip.loading = false;
      const payload = action.payload as { message?: string } | undefined;
      state.downloadInvoicesZip.error =
        payload?.message ?? action.error.message ?? 'Failed to download invoices zip';
    });

    builder.addCase(logout.fulfilled, () => initialState);
    builder.addCase(switchWorkspace, () => initialState);
  },
});

export const { clearSubscription, setCheckoutPromoCode, reset: resetBilling } = slice.actions;
export default slice.reducer;

// Subscription
export const selectSubscription = (state: { billing: BillingState }) =>
  state.billing.subscription.data;
export const selectSubscriptionState = (state: { billing: BillingState }) =>
  state.billing.subscription;

// Usage
export const selectUsage = (state: { billing: BillingState }) => state.billing.usage.data;
export const selectUsageState = (state: { billing: BillingState }) => state.billing.usage;

// Upgrade preview
export const selectUpgradePreview = (state: { billing: BillingState }) =>
  state.billing.upgradePreview.data;
export const selectUpgradePreviewState = (state: { billing: BillingState }) =>
  state.billing.upgradePreview;

// Downgrade impact preview
export const selectDowngradeImpactPreview = (state: { billing: BillingState }) =>
  state.billing.downgradeImpactPreview.data;
export const selectDowngradeImpactPreviewState = (state: { billing: BillingState }) =>
  state.billing.downgradeImpactPreview;

// Prices
export const selectPrices = (state: { billing: BillingState }) => state.billing.prices.data;
export const selectPricesState = (state: { billing: BillingState }) => state.billing.prices;

// Promo code
export const selectPromoCode = (state: { billing: BillingState }) => state.billing.promoCode.data;
export const selectPromoCodeState = (state: { billing: BillingState }) => state.billing.promoCode;
export const selectCheckoutPromoCode = (state: {
  billing: BillingState;
  workspace: { currentWorkspace: { promoCode?: string | null } | null };
}) =>
  resolveCheckoutPromoCode(
    state.billing.checkoutPromoCode,
    state.workspace.currentWorkspace?.promoCode,
  );

/**
 * Tarifs pas encore relus pour le code du tunnel : appliqué à l'instant, relecture en cours ou en
 * échec. Le montant affiché ne vaut pas pour ce code, on ne paie pas dessus.
 */
export const selectCheckoutPricesPending = (state: {
  billing: BillingState;
  workspace: { currentWorkspace: { promoCode?: string | null } | null };
}) =>
  state.billing.prices.loading ||
  !isSamePromoCode(state.billing.prices.promoCode, selectCheckoutPromoCode(state));

// Payment methods
export const selectPaymentMethods = (state: { billing: BillingState }) =>
  state.billing.paymentMethods.data;
export const selectPaymentMethodsState = (state: { billing: BillingState }) =>
  state.billing.paymentMethods;

// Invoices
export const selectInvoices = (state: { billing: BillingState }) => state.billing.invoices.data;
export const selectInvoicesState = (state: { billing: BillingState }) => state.billing.invoices;

// Address
export const selectAddress = (state: { billing: BillingState }) => state.billing.address.data;
export const selectAddressState = (state: { billing: BillingState }) => state.billing.address;

// Setup intent
export const selectSetupIntent = (state: { billing: BillingState }) =>
  state.billing.setupIntent.data;
export const selectSetupIntentState = (state: { billing: BillingState }) =>
  state.billing.setupIntent;

// Checkout
export const selectCheckout = (state: { billing: BillingState }) => state.billing.checkout.data;
export const selectCheckoutState = (state: { billing: BillingState }) => state.billing.checkout;

// Create subscription
export const selectCreateSubscription = (state: { billing: BillingState }) =>
  state.billing.createSubscription.data;
export const selectCreateSubscriptionState = (state: { billing: BillingState }) =>
  state.billing.createSubscription;

// Exit flow
export const selectExitFlow = (state: { billing: BillingState }) => state.billing.exitFlow.data;
export const selectExitFlowState = (state: { billing: BillingState }) => state.billing.exitFlow;

// Upgrade
export const selectUpgradeLoading = (state: { billing: BillingState }) =>
  state.billing.upgrade.loading;
export const selectUpgradeError = (state: { billing: BillingState }) => state.billing.upgrade.error;

// Resume subscription
export const selectResumeSubscriptionLoading = (state: { billing: BillingState }) =>
  state.billing.resumeSubscription.loading;
export const selectResumeSubscriptionError = (state: { billing: BillingState }) =>
  state.billing.resumeSubscription.error;

// Resume paused subscription
export const selectResumeSubscriptionPauseLoading = (state: { billing: BillingState }) =>
  state.billing.resumeSubscriptionPause.loading;
export const selectResumeSubscriptionPauseError = (state: { billing: BillingState }) =>
  state.billing.resumeSubscriptionPause.error;

// Set payment method default
export const selectSetDefaultLoading = (state: { billing: BillingState }) =>
  state.billing.setPaymentMethodDefault.loading;
export const selectSetDefaultError = (state: { billing: BillingState }) =>
  state.billing.setPaymentMethodDefault.error;

// Detach payment method
export const selectDetachPaymentMethodLoading = (state: { billing: BillingState }) =>
  state.billing.detachPaymentMethod.loading;
export const selectDetachPaymentMethodError = (state: { billing: BillingState }) =>
  state.billing.detachPaymentMethod.error;

// Download invoices zip
export const selectDownloadInvoicesZipLoading = (state: { billing: BillingState }) =>
  state.billing.downloadInvoicesZip.loading;
export const selectDownloadInvoicesZipError = (state: { billing: BillingState }) =>
  state.billing.downloadInvoicesZip.error;

// Legacy aliases (rétrocompatibilité si utilisé ailleurs)
export const selectBillingLoading = (state: { billing: BillingState }) =>
  state.billing.subscription.loading;
export const selectBillingError = (state: { billing: BillingState }) =>
  state.billing.subscription.error;
export const selectUsageLoading = (state: { billing: BillingState }) => state.billing.usage.loading;
export const selectUsageError = (state: { billing: BillingState }) => state.billing.usage.error;
